import { useState, useCallback, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  YARDI_SEARCH_GUEST_SESSION_KEY,
  GUEST_ALLOWED_ROLES,
  INITIAL_URL_PARAMS,
  GUEST_SEARCH_SESSION_OVERLAY_KEYS,
} from '../constants'
import {
  buildTrimmedParams,
  validateData,
  formatDateYardi,
  hasAtLeastOneYardiGuestSearchCriterion,
  isYardiGuestSearchCriteriaValidationError,
} from '../utils/yardiInviteFormatters'
import {
  fetchGuestSearchCompanies,
  fetchGuestSearchProperties,
  searchGuestCards,
} from '../services/yardiInviteService'
import type { YardiGuestCardRow, YardiGuestSearchForm } from '../types'
import { readSessionJson, writeSessionJson, clearSessionKey, stripMeta } from './sessionFilters'
import { showError, formatApiError } from '@/lib/utils/notifications'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { yardiGuestSearchSchema } from '../utils/yardiInviteValidators'

export function useYardiGuestSearch(opts: {
  roleName: string
  orgId: string | null
  companyId: string | null
  propertyId: string | null
  orgName: string | null
  companyName: string | null
  propertyName: string | null
}) {
  const navigate = useNavigate()
  const [invites, setInvites] = useState<YardiGuestCardRow[] | null>(null)
  /** True only while `loadInvites` runs; not tied to dropdown fetches. */
  const [processing, setProcessing] = useState(false)
  /** Same window as `processing` — guest-card search API only. */
  const [resultsLoading, setResultsLoading] = useState(false)
  const [companyOptions, setCompanyOptions] = useState<{ value: string; label: string }[]>([])
  const [propertyOptions, setPropertyOptions] = useState<{ value: string; label: string }[]>([])
  const [readOnlyControl, setReadOnlyControl] = useState<string[]>([])
  const [guestSearchCriteriaModalOpen, setGuestSearchCriteriaModalOpen] = useState(false)
  const [windowHeight, setWindowHeight] = useState(
    typeof window !== 'undefined' ? window.innerHeight : 800,
  )
  const onPageLoadRef = useRef(true)
  /** Guest list fetch id sequence — kept separate from dropdown loaders. */
  const listFetchRequestIdRef = useRef(0)
  const companyRequestIdRef = useRef(0)
  const propertyRequestIdRef = useRef(0)
  const allPropertyRef = useRef<Record<string, string>>({})
  const allCompanyRef = useRef<Record<string, string>>({})
  const allOrgRef = useRef<Record<string, string>>({})

  const form = useForm<YardiGuestSearchForm>({
    resolver: zodResolver(yardiGuestSearchSchema),
    /** Validate on explicit submit, not on the initial load gate. */
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues: {
      org_id: '',
      company_id: '',
      property_id: '',
      first_name: '',
      last_name: '',
      email_address: '',
      phone_number: '',
      date_of_birth: '',
      third_party_id: '',
      federal_id: '',
      join_filter_items_with_and: false,
    },
  })

  const urlParamsRef = useRef<Record<string, string | number | boolean>>({})

  const getAccessiblePermission = useCallback(() => {
    const sessionFilter = readSessionJson<Record<string, unknown>>(YARDI_SEARCH_GUEST_SESSION_KEY)
    if (opts.orgId) {
      setReadOnlyControl((r) => [...new Set([...r, 'organization'])])
      if (!(sessionFilter && sessionFilter.org_id)) {
        allOrgRef.current[opts.orgId] = opts.orgName || opts.orgId
        form.setValue('org_id', opts.orgId, { shouldValidate: false })
      }
    }
    if (opts.companyId) {
      setReadOnlyControl((r) => [...new Set([...r, 'company'])])
      setCompanyOptions([{ value: opts.companyId, label: opts.companyName || opts.companyId }])
      allCompanyRef.current[opts.companyId] = opts.companyName || opts.companyId
      form.setValue('company_id', opts.companyId, { shouldValidate: false })
    }
    if (opts.propertyId) {
      setReadOnlyControl((r) => [...new Set([...r, 'property'])])
      setPropertyOptions([{ value: opts.propertyId, label: opts.propertyName || opts.propertyId }])
      allPropertyRef.current[opts.propertyId] = opts.propertyName || opts.propertyId
      form.setValue('property_id', opts.propertyId, { shouldValidate: false })
    }
  }, [opts.orgId, opts.companyId, opts.propertyId, opts.orgName, opts.companyName, opts.propertyName, form])

  const setDefaultRequestParam = useCallback(() => {
    const DEFAULTS: Record<string, string | boolean> = {
      q: '',
      org_id: '',
      company_id: '',
      property_id: '',
      first_name: '',
      last_name: '',
      email_address: '',
      phone_number: '',
      date_of_birth: '',
      third_party_id: '',
      join_filter_items_with_and: false,
      federal_id: '',
    }
    const sessionFilter = readSessionJson<Record<string, unknown>>(YARDI_SEARCH_GUEST_SESSION_KEY)
    if (sessionFilter) {
      if (sessionFilter.org_id && sessionFilter.org_name) {
        allOrgRef.current[String(sessionFilter.org_id)] = String(sessionFilter.org_name)
      } else if (sessionFilter.org_id) {
        allOrgRef.current[String(sessionFilter.org_id)] = String(sessionFilter.org_id)
      }
      if (sessionFilter.property_id && sessionFilter.property_name) {
        setPropertyOptions([
          { value: String(sessionFilter.property_id), label: String(sessionFilter.property_name) },
        ])
        allPropertyRef.current[String(sessionFilter.property_id)] = String(sessionFilter.property_name)
      }
      if (sessionFilter.company_id && sessionFilter.company_name) {
        setCompanyOptions([
          { value: String(sessionFilter.company_id), label: String(sessionFilter.company_name) },
        ])
        allCompanyRef.current[String(sessionFilter.company_id)] = String(sessionFilter.company_name)
      }
      Object.assign(DEFAULTS, stripMeta(sessionFilter) as Record<string, string | boolean>)
    }
    const dobRaw = DEFAULTS.date_of_birth
    const dobStr =
      dobRaw && String(dobRaw).length > 0 ? formatDateYardi(dobRaw as string | Date) : ''
    form.reset({
      org_id: String(DEFAULTS.org_id ?? ''),
      company_id: String(DEFAULTS.company_id ?? ''),
      property_id: String(DEFAULTS.property_id ?? ''),
      first_name: String(DEFAULTS.first_name ?? ''),
      last_name: String(DEFAULTS.last_name ?? ''),
      email_address: String(DEFAULTS.email_address ?? ''),
      phone_number: String(DEFAULTS.phone_number ?? ''),
      date_of_birth: dobStr,
      third_party_id: String(DEFAULTS.third_party_id ?? ''),
      federal_id: String(DEFAULTS.federal_id ?? ''),
      join_filter_items_with_and: Boolean(DEFAULTS.join_filter_items_with_and),
    })
    form.clearErrors()
    getAccessiblePermission()
  }, [form, getAccessiblePermission])

  const getFilterValue = useCallback(() => {
    const vals = form.getValues()
    return buildTrimmedParams(vals as unknown as Record<string, unknown>)
  }, [form])

  const saveFilter = useCallback(() => {
    const filterData: Record<string, unknown> = { ...urlParamsRef.current }
    const pid = filterData.property_id
    if (pid && allPropertyRef.current[String(pid)]) {
      filterData.property_name = allPropertyRef.current[String(pid)]
    }
    const cid = filterData.company_id
    if (cid && allCompanyRef.current[String(cid)]) {
      filterData.company_name = allCompanyRef.current[String(cid)]
    }
    const oid = filterData.org_id
    if (oid && allOrgRef.current[String(oid)]) {
      filterData.org_name = allOrgRef.current[String(oid)]
    }
    writeSessionJson(YARDI_SEARCH_GUEST_SESSION_KEY, filterData)
  }, [])

  const loadInvites = useCallback(async () => {
    saveFilter()
    /** Silent gate — no `form.trigger()` here to avoid flashing errors. */
    const parsed = yardiGuestSearchSchema.safeParse(form.getValues())
    if (!parsed.success) {
      return
    }
    const trimmed = urlParamsRef.current as Record<string, string | number | boolean>
    if (!hasAtLeastOneYardiGuestSearchCriterion(trimmed)) {
      setGuestSearchCriteriaModalOpen(true)
      return
    }
    const id = ++listFetchRequestIdRef.current
    setProcessing(true)
    setResultsLoading(true)
    try {
      const res = await searchGuestCards(
        urlParamsRef.current as Record<string, string | number | boolean>,
      )
      if (id !== listFetchRequestIdRef.current) return
      setInvites(res)
    } catch (e) {
      if (isYardiGuestSearchCriteriaValidationError(e)) {
        setGuestSearchCriteriaModalOpen(true)
        return
      }
      showError(formatApiError(e), false)
    } finally {
      if (id === listFetchRequestIdRef.current) {
        setProcessing(false)
        setResultsLoading(false)
      }
    }
  }, [form, saveFilter])

  const triggerFilter = useCallback(() => {
    urlParamsRef.current = {}
    const sessionFilter = readSessionJson<Record<string, unknown>>(YARDI_SEARCH_GUEST_SESSION_KEY)
    if (onPageLoadRef.current && sessionFilter) {
      onPageLoadRef.current = false
      Object.assign(urlParamsRef.current, validateData({ ...sessionFilter }) as Record<string, string | number | boolean>)
    }
    const next = urlParamsRef.current as Record<string, unknown>
    for (const key of GUEST_SEARCH_SESSION_OVERLAY_KEYS) {
      delete next[key]
    }
    Object.assign(
      urlParamsRef.current,
      validateData({ ...getFilterValue() }) as Record<string, string | number | boolean>,
    )
    window.setTimeout(() => {
      void loadInvites()
    }, 2)
  }, [getFilterValue, loadInvites])

  useEffect(() => {
    if (!GUEST_ALLOWED_ROLES.includes(opts.roleName)) {
      navigate('/dashboard')
      return
    }
    Object.assign(urlParamsRef.current, INITIAL_URL_PARAMS)
    setDefaultRequestParam()
    triggerFilter()
    loadCompany('')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const onResize = (e: UIEvent) => {
      const t = e.target as Window
      setWindowHeight(t.innerHeight)
    }
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  const loadProperty = useCallback(
    async (searchText?: string) => {
      const id = ++propertyRequestIdRef.current
      try {
        const orgId = form.getValues('org_id') || undefined
        const companyId = form.getValues('company_id') || undefined
        const rows = await fetchGuestSearchProperties(searchText, companyId, orgId)
        if (id !== propertyRequestIdRef.current) return
        const opts = rows.map((p) => ({
          value: String(p.property_id),
          label: p.property_name || String(p.property_id),
        }))
        rows.forEach((p) => {
          allPropertyRef.current[String(p.property_id)] = p.property_name || String(p.property_id)
        })
        setPropertyOptions(opts)
        const searchEmpty = !searchText?.trim()
        window.setTimeout(() => {
          if (id !== propertyRequestIdRef.current) return
          if (opts.length === 1) {
            form.setValue('property_id', opts[0].value, { shouldValidate: true, shouldDirty: true })
            form.clearErrors('property_id')
            return
          }
          /** Keep `property_id` only if present in this response; otherwise clear it. */
          if (searchEmpty) {
            const cur = form.getValues('property_id')
            if (cur) {
              const has = opts.some((o) => o.value === cur)
              if (has) {
                form.setValue('property_id', cur, { shouldValidate: true, shouldDirty: true })
                form.clearErrors('property_id')
              } else {
                form.setValue('property_id', '', { shouldValidate: true, shouldDirty: true })
                form.clearErrors('property_id')
              }
            }
          }
        }, 10)
      } catch (e) {
        showError(formatApiError(e), false)
      }
    },
    [form],
  )

  const loadCompany = useCallback(
    async (searchText?: string) => {
      const id = ++companyRequestIdRef.current
      try {
        const orgId = form.getValues('org_id') || undefined
        const rows = await fetchGuestSearchCompanies(searchText, orgId)
        if (id !== companyRequestIdRef.current) return
        const opts = rows.map((c) => ({
          value: String(c.company_id),
          label: c.company_name || String(c.company_id),
        }))
        rows.forEach((c) => {
          allCompanyRef.current[String(c.company_id)] = c.company_name || String(c.company_id)
        })
        setCompanyOptions(opts)
        /** Single company → auto-select and re-validate. */
        if (opts.length === 1) {
          window.setTimeout(() => {
            if (id !== companyRequestIdRef.current) return
            const v = opts[0].value
            form.setValue('company_id', v, { shouldValidate: true, shouldDirty: true })
            form.clearErrors('company_id')
          }, 10)
        }
      } catch (e) {
        showError(formatApiError(e), false)
      }
    },
    [form],
  )

  const searchProperty = useCallback(
    (text: string) => {
      void loadProperty(text)
    },
    [loadProperty],
  )

  const searchCompany = useCallback(
    (text: string) => {
      void loadCompany(text)
    },
    [loadCompany],
  )

  /** Unless property is role-locked, reload the property list for the selected company. */
  const companySelected = useCallback(
    (_companyId: string) => {
      if (opts.propertyId) return
      setPropertyOptions([])
      void loadProperty('')
    },
    [opts.propertyId, loadProperty],
  )

  const pasteData = useCallback(
    (text: string, type: 'company' | 'property') => {
      if (type === 'company') loadCompany(text)
      else loadProperty(text)
    },
    [loadCompany, loadProperty],
  )

  const dismissGuestSearchCriteriaModal = useCallback(() => {
    setGuestSearchCriteriaModalOpen(false)
  }, [])

  const filterSubmit = useCallback(
    (options?: { onApplied?: () => void }) => {
      void form.handleSubmit(
        () => {
          triggerFilter()
          options?.onApplied?.()
        },
        () => {
          void form.trigger()
        },
      )()
    },
    [form, triggerFilter],
  )

  const resetFilter = useCallback(() => {
    Object.assign(urlParamsRef.current, INITIAL_URL_PARAMS)
    clearSessionKey(YARDI_SEARCH_GUEST_SESSION_KEY)
    onPageLoadRef.current = true
    setDefaultRequestParam()
    triggerFilter()
  }, [setDefaultRequestParam, triggerFilter])

  return {
    form,
    invites,
    processing,
    resultsLoading,
    companyOptions,
    propertyOptions,
    readOnlyControl,
    windowHeight,
    filterSubmit,
    resetFilter,
    searchCompany,
    searchProperty,
    companySelected,
    pasteData,
    loadCompany,
    loadProperty,
    guestSearchCriteriaModalOpen,
    dismissGuestSearchCriteriaModal,
  }
}
